import {useState} from "react";
import {TextField} from "@mui/material";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {AddEmailRecipients} from "../AddEmailRecipients";
import {useDocumentTranslation} from "../../documents/documentContext";
import {clientLog} from "../../clientLog";
import {useSnackbar} from "notistack";
import {shareDeliveryMessage} from "../shareDelivery";
import {postShare} from "../postShare";
import {shareDeliverySnackbarOptions} from "../shareDeliverySnackbar";

/**
 * Panel for sharing a document publicly via a time-limited portal link.
 * Shown in the share dialog (ModelSharingDialog).
 *
 * @param {Object}   model           - The document model ({ uniqueId, title }).
 * @param {Function} onShareSuccess  - Called after a successful share so the
 *                                     history panel can refresh.
 */
export const SharePublicPanel = ({model, onShareSuccess}) => {
    const t = useDocumentTranslation();
    const {enqueueSnackbar} = useSnackbar();

    const {uniqueId, title} = model || {};

    const [message, setMessage] = useState('');
    const [recipients, setRecipients] = useState([]);
    const [sharing, setSharing] = useState(false);

    const _handleShareClick = async () => {
        if (!recipients?.length) return;
        setSharing(true);
        try {
            const delivery = await postShare({
                object_uuids: [uniqueId],
                recipient_emails: recipients,
                email_message: message !== '' ? message : null
            });
            enqueueSnackbar(shareDeliveryMessage(t, delivery), shareDeliverySnackbarOptions(t, delivery));
            setMessage('');
            setRecipients([]);
            if (onShareSuccess) onShareSuccess();
        } catch (error) {
            clientLog('error', error.toString(), 'sharepublicpanel');
            enqueueSnackbar(t('fileSharePubliclyFailed', 'Deling av filen offentlig feilet'), {variant: 'error'});
        } finally {
            setSharing(false);
        }
    };

    if (sharing) {
        return (
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4, gap: 2}}>
                <LinearProgress variant="indeterminate" sx={{width: '100%'}}/>
                <Typography>{t('dialogShareFilePubliclySharingProcess', 'Deler filen')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{pt: 1}}>
            <Typography sx={{mb: 1}}>
                {t(
                    'dialogShareFilePubliclyText',
                    'Du er nå i ferd med å dele {{title}} offentlig.\n' +
                    'Dette betyr at alle som har lenken til dette objektet vil kunne se det.',
                    {title}
                )}
            </Typography>
            <TextField
                helperText={t(
                    'dialogShareFilePubliclyAddText',
                    'Legg til en melding som skal sendes til mottagerne av delingen'
                )}
                label={t("shareDocumentLabelMessage", "Melding")}
                fullWidth
                margin="normal"
                multiline
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={4}
            />
            <AddEmailRecipients callback={setRecipients} t={t} initialRecipients={recipients}/>
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                <Button
                    variant="contained"
                    disabled={recipients.length === 0}
                    onClick={_handleShareClick}
                >
                    {t('btnShare', 'Del')}
                </Button>
            </Box>
        </Box>
    );
};

